import { useFlags } from '../../firebase/FirebaseRemoteConfig.provider'
import { useEssentials } from 'hooks/utils/useEssentials'
import { isStore } from 'utils/location'
import { checkRoutePermissions } from 'utils/authentication'

type RouteFeatureFlag =
    | 'insightsEnabled'
    | 'salesLinkEnabled'
    | 'payrollEnabled'
    | 'myTargetPerformanceEnabled'
    | 'safetyEnabled'
    | 'shortageEnabled'
    | 'assetsProtectionEnabled'
    | 'loyaltyEnabled'
    | 'trailerEnabled'
    | 'brandsEnabled'
    | 'storeFulfillmentEnabled'
    | 'opsMetricsEnabled'
    | 'itemDetailsEnabled'
    | 'notesEnabled'
    | 'storeInfoEnabled'
    | 'settingsLinkEnabled'
    | 'powerBiEnabled'

interface RouteAccessRule {
    featureFlag: RouteFeatureFlag
    storeOnly?: boolean
}

const routeAccessRules: Record<string, RouteAccessRule> = {
    '/insights': {
        featureFlag: 'insightsEnabled',
    },
    '/sales': { featureFlag: 'salesLinkEnabled' },
    '/payroll': { featureFlag: 'payrollEnabled' },
    '/mytarget-performance': {
        featureFlag: 'myTargetPerformanceEnabled',
    },
    '/safety': { featureFlag: 'safetyEnabled' },
    '/shortage': { featureFlag: 'shortageEnabled' },
    '/assets-protection': {
        featureFlag: 'assetsProtectionEnabled',
    },
    '/loyalty': { featureFlag: 'loyaltyEnabled' },
    '/trailer': { featureFlag: 'trailerEnabled' },
    '/brands': { featureFlag: 'brandsEnabled' },
    '/store-fulfillment': {
        featureFlag: 'storeFulfillmentEnabled',
    },
    '/operations': { featureFlag: 'opsMetricsEnabled' },
    '/item-search': {
        featureFlag: 'itemDetailsEnabled',
        storeOnly: true,
    },
    '/notes': { featureFlag: 'notesEnabled', storeOnly: true },
    '/store-facts': { featureFlag: 'storeInfoEnabled', storeOnly: true },
    '/settings': { featureFlag: 'settingsLinkEnabled' },
    '/power-bi': { featureFlag: 'powerBiEnabled' },
}

const getRouteRule = (path: string) =>
    Object.entries(routeAccessRules).find(
        ([route]) => path === route || path.startsWith(`${route}/`),
    )?.[1]

/**
 * Applies the same feature, location, and permission rules to nav links and
 * route entry. Routes without a side-nav item continue to use their own guards.
 */
export const useRouteAccess = () => {
    const { currentLocation, greenfieldGroups, env } = useEssentials()
    const flags = useFlags()
    const storeLocation = isStore(currentLocation)

    return (path: string, checkPermissions = true) => {
        let permissionPath = path
        // Strip path down to a known permission key (e.g. /mytarget-performance/details/metric → /mytarget-performance)
        while (
            permissionPath.lastIndexOf('/') > 0 &&
            !env.permissionsByKPI[permissionPath]
        ) {
            permissionPath = permissionPath.slice(
                0,
                permissionPath.lastIndexOf('/'),
            )
        }
        const rule = getRouteRule(permissionPath)

        if (!rule) {
            return checkRoutePermissions({
                path: permissionPath,
                userPermissions: greenfieldGroups,
                permissionsByKPI: env.permissionsByKPI,
            })
        }

        if (!flags[rule.featureFlag] || (rule.storeOnly && !storeLocation)) {
            return false
        }

        if (!checkPermissions) return true

        return checkRoutePermissions({
            path: permissionPath,
            userPermissions: greenfieldGroups,
            permissionsByKPI: env.permissionsByKPI,
        })
    }
}
